/**
 * Intro-animasjon: den leseren ikke styrer.
 *
 * Én sammenhengende flytur fra busskuret til Aker Brygge, med en
 * høydebue og uten mellomstopp. Bussruta tegnes mens kameraet flyr.
 *
 * Rutene tegnes ved å klippe kildedataene (`sampler.sliceTo`) og sette dem
 * på nytt hver frame. Alternativet, `line-gradient`, krever `lineMetrics`
 * på kilden – og den endrer hvordan `line-dasharray` skalerer, noe som ville
 * ødelagt de stiplede tunnelstrekkene. Klipping beholder `structure` per
 * segment, så tunnelene fortsetter å filtreres til sitt eget stiplede lag
 * mens ruta vokser fram.
 */

import { BUS_ROUTE, CAR_ROUTE } from '../data/route.js';
import { ROUTE_SAMPLERS, setRouteVisibility } from './route.js';
import { INTRO_SHOTS, INTRO2_END, INTRO3_END, INTRO4_END, INTRO5_START, INTRO5_END, INTRO5_MOBILE_START, INTRO5_MOBILE_END, INTRO6_START, INTRO6_END, INTRO6_MOBILE_START, INTRO6_MOBILE_END, INTRO7_START, INTRO7_END, INTRO7_MOBILE_START, INTRO7_MOBILE_END, introProgressAtTime, sampleIntroCamera, sampleIntro2Camera, sampleIntro3Camera, sampleIntro4Camera, sampleIntro5Camera, sampleIntro5CameraMobile, sampleIntro6Camera, sampleIntro6CameraMobile, sampleIntro7Camera, sampleIntro7CameraMobile } from './intro-path.js';
export { INTRO_SHOTS } from './intro-path.js';

/**
 * Hvilke ruter introen etablerer. Bare bussen: to linjer som vokser samtidig
 * konkurrerer om blikket.
 *
 * Bilruta skjules når introen starter og blir VÆRENDE skjult etterpå. Å
 * legge den tilbake til slutt ga et hopp der den gule linja plutselig lå
 * der - verre enn at den mangler. Den slås på igjen med avkrysningsboksen.
 */
const INTRO_MODES = ['bus'];

const HOLD_MS = 0;          // ingen pust – kameraet løfter seg umiddelbart
const DRAW_DELAY_MS = 0;    // streken tegnes fra første øyeblikk
const DRAW_MS = 7500;
const FULL = { bus: BUS_ROUTE, car: CAR_ROUTE };

/** Mykt inn og ut, så opptegningen ikke starter og stopper brått. */
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export function createIntro(map, opts = {}) {
  // onDraw(t): valgfri callback som får rute-opptegningens fremdrift (0–1)
  // hver frame. Brukes av intro 7 til å flytte bussen + markøren langs ruta
  // i takt med at den grønne streken vokser.
  const onDraw = opts.onDraw ?? null;
  const onStart = opts.onStart ?? null;
  const onEnd = opts.onEnd ?? null;
  let generation = 0;
  let running = false;
  let previousGroundClamp = null;

  function restoreGroundClamp() {
    if (previousGroundClamp === null) return;
    map.setCenterClampedToGround(previousGroundClamp);
    previousGroundClamp = null;
  }

  /** Tegn introrutene `t` av veien (0–1); skjul dem som ikke er med. */
  function drawTo(t) {
    for (const mode of INTRO_MODES) {
      const sampler = ROUTE_SAMPLERS[mode];
      map.getSource(`route-${mode}`)
        ?.setData(t >= 1 ? FULL[mode] : sampler.sliceTo(t * sampler.length));
    }
  }

  /** Full geometri tilbake på de rutene introen tegnet. Bilruta røres ikke:
   *  den er skjult via `visibility` og skal slås på fra avkrysningsboksen. */
  function restoreRoutes() {
    for (const mode of INTRO_MODES) map.getSource(`route-${mode}`)?.setData(FULL[mode]);
  }

  /** Leseren skal ikke kunne gripe inn mens introen går. */
  function setInteractive(on) {
    for (const h of ['dragPan', 'scrollZoom', 'boxZoom', 'dragRotate',
      'keyboard', 'doubleClickZoom', 'touchZoomRotate']) {
      if (on) map[h]?.enable(); else map[h]?.disable();
    }
  }

  /** Kamera og rutetegning deler én klokke, uten separate easeTo-etapper. */
  function animateFlight(token, sampleCamera) {
    return new Promise((resolve) => {
      const started = performance.now();
      const startElevation = map.getCenterElevation();
      const elevationAt = sampleCamera !== sampleIntroCamera
        ? () => startElevation
        : (center) => map.queryTerrainElevation(center) ?? map.getCenterElevation();
      let lastDrawProgress = 0;
      const step = (now) => {
        if (token !== generation) return resolve();
        const elapsed = now - started;
        const t = introProgressAtTime(elapsed);
        map.jumpTo(sampleCamera(t, elevationAt, {
            height: map.getContainer().clientHeight,
            fov: map.getVerticalFieldOfView(),
          }));
        const drawProgress = Math.max(0, Math.min(1, (elapsed - DRAW_DELAY_MS) / DRAW_MS));
        if (drawProgress !== lastDrawProgress) {
          const eased = easeInOut(drawProgress);
          drawTo(eased);
          onDraw?.(eased);
          lastDrawProgress = drawProgress;
        }
        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  }

  async function play(variant = 'intro') {
    const [sampleCamera, endCamera, startShot] = {
      intro: [sampleIntroCamera, INTRO_SHOTS.clocktower, INTRO_SHOTS.start],
      intro2: [sampleIntro2Camera, INTRO2_END, INTRO_SHOTS.start],
      intro3: [sampleIntro3Camera, INTRO3_END, INTRO_SHOTS.start],
      intro4: [sampleIntro4Camera, INTRO4_END, INTRO_SHOTS.start],
      intro5: [sampleIntro5Camera, INTRO5_END, INTRO5_START],
      intro5mobile: [sampleIntro5CameraMobile, INTRO5_MOBILE_END, INTRO5_MOBILE_START],
      intro6: [sampleIntro6Camera, INTRO6_END, INTRO6_START],
      intro6mobile: [sampleIntro6CameraMobile, INTRO6_MOBILE_END, INTRO6_MOBILE_START],
      intro7: [sampleIntro7Camera, INTRO7_END, INTRO7_START],
      intro7mobile: [sampleIntro7CameraMobile, INTRO7_MOBILE_END, INTRO7_MOBILE_START],
    }[variant];
    const token = ++generation;
    running = true;
    setInteractive(false);

    // Respekter systemets «reduser bevegelse»: hopp rett til sluttbildet
    // med ruta ferdig tegnet, i stedet for å tvinge fram en lang flytur.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      restoreRoutes();
      map.jumpTo(endCamera);
      running = false;
      setInteractive(true);
      return;
    }

    try {
      onStart?.(variant);
      for (const mode of ['bus', 'car']) {
        if (!INTRO_MODES.includes(mode)) setRouteVisibility(map, mode, false);
      }
      drawTo(0);
      onDraw?.(0);
      map.jumpTo(startShot);
      await wait(HOLD_MS);
      if (token !== generation) return;

      if (variant !== 'intro') {
        previousGroundClamp = map.getCenterClampedToGround();
        map.setCenterClampedToGround(false);
      }
      await animateFlight(token, sampleCamera);
    } finally {
      if (token === generation) {
        restoreGroundClamp();
        restoreRoutes();
        onEnd?.(variant);
        running = false;
        setInteractive(true);
      }
    }
  }

  /** Avbryt og sett kartet tilbake til utforskbar tilstand. */
  function stop() {
    generation++;
    running = false;
    map.stop();
    restoreGroundClamp();
    restoreRoutes();
    setInteractive(true);
  }

  return { play, stop, get running() { return running; } };
}

/**
 * Kobler en nedtrekksmeny + spill/stopp-knapp til introspilleren.
 * Enkelt dev-verktøy (ikke produksjon): velg intro i menyen, klikk «Spill».
 *
 * @param {HTMLSelectElement} select   meny med intro-variantene
 * @param {HTMLButtonElement} button   spill/stopp-knapp
 * @param {object} [callbacks]         { onDraw, onStart, onEnd } videre til createIntro
 */
export function wireIntro(map, select, button, callbacks = {}) {
  if (!select || !button) return null;
  const intro = createIntro(map, callbacks);
  // Egne (ikke-intro) scener kan håndteres av kalleren via onCustomPlay/Stop.
  const custom = callbacks.custom ?? {}; // { scene8: { play(), stop() } }

  const VARIANTS = [
    ['intro', 'Intro 1'], ['intro2', 'Intro 2'], ['intro3', 'Intro 3'],
    ['intro4', 'Intro 4'], ['intro5', 'Intro 5'], ['intro6', 'Intro 6'],
    ['intro7', 'Intro 7 (buss)'],
    ...Object.keys(custom).map((k) => [k, custom[k].label ?? k]),
  ];
  select.innerHTML = '';
  for (const [value, label] of VARIANTS) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    select.appendChild(opt);
  }
  select.value = custom.scene8 ? 'scene8' : 'intro7';

  let sequence = 0;
  let activeCustom = null;
  button.addEventListener('click', async () => {
    const token = ++sequence;
    // Stopp alt som kjører (intro eller custom-scene).
    if (intro.running || activeCustom) {
      intro.stop();
      activeCustom?.stop();
      activeCustom = null;
      button.textContent = 'Spill';
      return;
    }
    const variant = select.value;
    button.textContent = 'Stopp';
    if (custom[variant]) {
      activeCustom = custom[variant];
      custom[variant].play();
      return; // custom-scener styrer sin egen livssyklus
    }
    try {
      await intro.play(variant);
    } finally {
      if (token === sequence) button.textContent = 'Spill';
    }
  });
  return intro;
}
